import React from 'react';

import { getWorkflowAiLifecycleState } from '@aline/common';
import { link, routes } from '@aline/routes';
import { matchPath, Navigate, useLocation } from 'react-router-dom';
import { NOT_FOUND_ENTITIES, NotFoundState } from 'src/components/not-found/NotFoundState';
import { useWorkflowParams } from 'src/helpers';
import { useGetWorkflowSimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { PhasesSection } from './phases/PhasesSection';
import { WorkflowAiBuildAlert } from './WorkflowAiBuildAlert';
import { WorkflowHeaderMeta, WorkflowHeaderTitleRow } from './WorkflowHeader';
import { WorkflowSummaryCards } from './WorkflowSummaryCards';
import { WorkflowTemplateToolbar } from '../workflow-builder';

const useStyles = makeStyles()(theme => ({
  mainContent: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 680,
  },
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'auto',
    width: '100%',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
  },
  sectionInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: '0 auto',
    maxWidth: 900,
    padding: theme.spacing(0, 3, 3, 3),
    width: '100%',
  },
}));

export function WorkflowDetailPage() {
  const { classes } = useStyles();
  const { workflowId } = useWorkflowParams();
  const { pathname } = useLocation();
  const { isNotFound, loading, workflow } = useGetWorkflowSimple(workflowId);
  const aiBuildState = workflow ? getWorkflowAiLifecycleState(workflow) : 'idle';
  const isAiBuilding = aiBuildState === 'building';
  const taskId = matchPath({ end: false, path: routes.WorkflowDetailTabTask }, pathname)?.params
    .taskId;

  /**
   * An external assignee can open their task but not the workflow around it.
   * That reads as a missing workflow, so this has to win over the not-found
   * state below.
   */
  if (!loading && !workflow && taskId)
    return <Navigate replace to={link.TaskLanding({ taskId })} />;

  /**
   * Every section below takes a `workflowId` and fetches for itself, so without
   * this the page renders a full shell of empty headers and cards for a
   * workflow that does not exist.
   */
  if (isNotFound)
    return (
      <div className={classes.root}>
        <NotFoundState entity={NOT_FOUND_ENTITIES.workflow} />
      </div>
    );

  return (
    <div className={classes.root}>
      <div className={classes.mainContent}>
        <WorkflowHeaderTitleRow workflowId={workflowId} />
        <WorkflowTemplateToolbar readOnly={isAiBuilding} workflowId={workflowId} />
        <div className={classes.scrollArea}>
          <div className={classes.sections}>
            <div className={classes.sectionInner}>
              <WorkflowHeaderMeta workflowId={workflowId} />
              {workflow && <WorkflowAiBuildAlert workflow={workflow} />}
              <WorkflowSummaryCards workflowId={workflowId} />
              <PhasesSection aiIsGenerating={isAiBuilding} workflowId={workflowId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
